import { useEffect, useState } from 'react';
import axios from 'axios';
import type { Domain } from 'vopay';
import vopayApi from '../../../services/api.config';
import useDataFetchIndicator from '../../../hooks/useDataFetchIndicator';

export default function useDomainSetup() {
  return vopayApi.withApiHook(({ api }) => {
    const source = axios.CancelToken.source();
    const [domain, setDomain] = useState<Domain | null>(null);
    const [, setDataFetchingInfo] = useDataFetchIndicator();
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
      fetchDomain();
    }, []);

    async function fetchDomain() {
      try {
        setDataFetchingInfo(true, 'fetching domains', 'fullScreen');
        setFetching(true);
        const res = await api.fetchDomain(source);

        if (!res.Success) {
          console.error(res.ErrorMessage);

          throw new Error(res.ErrorMessage);
        }

        setDomain(res.Domain);

        return res.Domain;
      } catch (err) {
        console.error(err);

        throw err;
      } finally {
        setDataFetchingInfo(false);
        setFetching(false);
      }
    }

    async function addDomain(url: string) {
      try {
        setDataFetchingInfo(true, 'adding domain', 'fullScreen');

        const res = await api.addDomain(
          {
            Domain: url,
          },
          source,
        );

        res.Domain && setDomain(res.Domain);

        return res;
      } finally {
        setDataFetchingInfo(false);
      }
    }

    async function deleteDomain(url: string) {
      try {
        setDataFetchingInfo(true, 'deleting domain', 'fullScreen');

        const res = await api.deleteDomain(
          {
            Domain: url,
          },
          source,
        );

        if (res.Success) {
          setDomain(null);
        }

        return res;
      } finally {
        setDataFetchingInfo(false);
      }
    }

    async function verifyDomain(url: string) {
      try {
        setDataFetchingInfo(true, 'verifying domain', 'fullScreen');

        const res = await api.verifyDomain(
          {
            Domain: url,
          },
          source,
        );

        res.Domain && setDomain(res.Domain);

        return res;
      } finally {
        setDataFetchingInfo(false);
      }
    }

    useEffect(() => {
      return () => source.cancel();
    }, []);

    return {
      fetching,
      domain,
      addDomain,
      deleteDomain,
      verifyDomain,
    };
  })({});
}
